<!--
  The demo request a lead gets wherever the reduced Hub stops: on locked views,
  and on the actions inside the views they can open. Reads as an invitation,
  not as a paywall.

  The request itself is HubSpot's form: it goes to HubSpot directly, our API
  isn't involved.

  Opened in a view (`inView`), closing it returns to that view instead of the
  Playground.
-->
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { Lock, X } from '@lucide/vue';
import { Button } from '@/components/ui/button';

const props = defineProps<{
  /** Translation key of the locked area's title, see `lockedAreaTitleKey` */
  areaTitleKey?: string | null;
  /** Opened by an action inside a view the lead can open, see `LeadDemoRequestOverlay` */
  inView?: boolean;
}>();
const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const title = computed(() => (props.areaTitleKey
  ? t('leadBot.demoRequest.titleArea', { area: t(props.areaTitleKey) })
  : t('leadBot.demoRequest.title')));

const backToPlayground = () => router.push({ name: 'ai' });
const close = () => (props.inView ? emit('close') : backToPlayground());
/** In the Playground itself, closing already leads back to it */
const showBackLink = computed(() => !props.inView || route.name !== 'ai');

/** HubSpot's demo request form, rendered by its embed script into each `.hs-form-frame` on the page */
const HUBSPOT_FORM = { region: 'eu1', portalId: '8374191', formId: 'ea73936b-4317-41a4-b667-43862c813149' };
const HUBSPOT_FORMS_SCRIPT = `https://js-${HUBSPOT_FORM.region}.hsforms.net/forms/embed/${HUBSPOT_FORM.portalId}.js`;

const hubspotForm = ref<HTMLElement | null>(null);
onMounted(() => {
  /*
   * The frame is added on its own, as the script only finds frames that are on the page when it loads, or
   * that are at most 5 levels deep in what's added to the page later. A whole locked view is added deeper.
   */
  const frame = document.createElement('div');
  frame.className = 'hs-form-frame';
  Object.assign(frame.dataset, HUBSPOT_FORM);
  hubspotForm.value?.appendChild(frame);

  // Once per page: loaded again, the script does nothing but warn
  if (document.querySelector(`script[src="${HUBSPOT_FORMS_SCRIPT}"]`)) return;

  const script = document.createElement('script');
  script.src = HUBSPOT_FORMS_SCRIPT;
  script.defer = true;
  document.body.appendChild(script);
});
</script>

<template>
  <section
    data-cy="lead-demo-request"
    aria-labelledby="lead-demo-request-title"
    class="relative w-full max-w-xl rounded-2xl bg-background p-8 shadow-2xl max-sm:p-5"
  >
    <Button
      data-cy="lead-demo-request-close"
      variant="ghost"
      size="icon-sm"
      class="absolute top-4 right-4"
      :aria-label="t('leadBot.demoRequest.close')"
      :title="t('leadBot.demoRequest.close')"
      @click="close"
    >
      <X />
    </Button>

    <span class="inline-flex items-center gap-1.5 rounded-full bg-mint-50 px-3 py-1 text-xs font-semibold tracking-wide text-mint-700 uppercase">
      <Lock class="size-3.5" aria-hidden="true" />
      {{ t('leadBot.demoRequest.badge') }}
    </span>

    <h1 id="lead-demo-request-title" class="mt-4 pr-8 text-2xl leading-tight font-semibold text-primary">{{ title }}</h1>
    <p class="mt-3 text-base text-muted-foreground">{{ t('leadBot.demoRequest.description') }}</p>

    <!-- HubSpot's form goes in here, see `onMounted` -->
    <div ref="hubspotForm" data-cy="lead-demo-request-form" class="mt-6" />

    <div v-if="showBackLink" class="mt-4 text-center">
      <Button data-cy="lead-demo-request-back" variant="link" @click="backToPlayground">
        {{ t('leadBot.demoRequest.back') }}
      </Button>
    </div>
  </section>
</template>
